/* Loupe – product compare.
   - Store: localStorage "loupe:compare" = [{ handle, title, image, url }] (max 4). Falls back to memory when storage is blocked.
   - Card toggles: [data-compare-toggle] buttons rendered by snippets/product-card.liquid (compare: true). They stay hidden
     until this file runs, so they never appear without the tray.
   - <loupe-compare-tray>: floating tray (sections/compare-tray.liquid, footer group).
   - <loupe-compare-page>: the comparison table (sections/main-compare.liquid on templates/page.compare.json). It renders the
     selected products with ONE Section Rendering API request:
       /search?q=handle:a OR handle:b OR mf:custom.frame_width&type=product&section_id=main-compare
     main-compare.liquid reads the handles and metafield keys back from search.terms (same trick as recently-viewed). */
(() => {
  if (window.LoupeCompare) return;

  const KEY = 'loupe:compare', MAX = 4;
  const okHandle = (h) => typeof h === 'string' && h.length > 0 && h.length < 256 && !/[\s,"'<>]/.test(h);
  let memory = [];

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw === null) return memory.slice();
      const l = JSON.parse(raw);
      return Array.isArray(l) ? l.filter((x) => x && okHandle(x.handle)).slice(0, MAX) : [];
    } catch (e) { return memory.slice(); }
  };
  const write = (l) => {
    memory = l.slice(0, MAX);
    try { localStorage.setItem(KEY, JSON.stringify(memory)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('loupe:compare-changed', { detail: { items: memory.slice() } }));
  };

  const api = {
    max: MAX,
    items: read,
    has: (h) => read().some((x) => x.handle === h),
    add(item) {
      const l = read();
      if (!item || !okHandle(item.handle)) return false;
      if (l.some((x) => x.handle === item.handle)) return true;
      if (l.length >= MAX) return false;
      l.push({ handle: item.handle, title: item.title || item.handle, image: item.image || '', url: item.url || '' });
      write(l); return true;
    },
    remove(h) { write(read().filter((x) => x.handle !== h)); },
    clear() { write([]); },
    set(list) { write(list.filter((x) => x && okHandle(x.handle))); }
  };
  window.LoupeCompare = api;

  const fill = (str, map) => Object.keys(map).reduce((s, k) => s.split(`[${k}]`).join(map[k]), str || '');
  const strings = (root) => {
    const el = root.querySelector('script[data-compare-strings]');
    try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
  };
  const iconClose = '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  const announce = (msg) => {
    if (!msg) return;
    const tray = document.querySelector('loupe-compare-tray');
    if (tray && tray.say) return tray.say(msg);
    const t = document.querySelector('[data-toast]');
    if (t) { t.textContent = msg; t.hidden = false; clearTimeout(t._t); t._t = setTimeout(() => (t.hidden = true), 2600); }
  };

  /* ---------- card toggles ---------- */
  const syncToggles = () => {
    const set = new Set(read().map((x) => x.handle));
    document.querySelectorAll('[data-compare-toggle]').forEach((b) => {
      b.hidden = false;
      b.setAttribute('aria-pressed', String(set.has(b.dataset.handle)));
    });
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-compare-toggle]'); if (!b) return;
    e.preventDefault();
    const h = b.dataset.handle, title = b.dataset.title || h;
    const s = (document.querySelector('loupe-compare-tray') || {}).s || {};
    if (api.has(h)) { api.remove(h); announce(fill(s.removed, { title })); }
    else if (api.add({ handle: h, title, image: b.dataset.image, url: b.dataset.url })) announce(fill(s.added, { title }));
    else {
      announce(fill(s.limit, { max: MAX }));
      const tray = document.querySelector('loupe-compare-tray'); if (tray && tray.nudge) tray.nudge();
    }
  });
  document.addEventListener('loupe:compare-changed', syncToggles);
  addEventListener('storage', (e) => { if (e.key === KEY) document.dispatchEvent(new CustomEvent('loupe:compare-changed', { detail: { items: read() } })); });
  /* cards added later (filters, load more, recently viewed, quick view) */
  let queued = false;
  new MutationObserver((ms) => {
    if (queued) return;
    for (const m of ms) for (const n of m.addedNodes) {
      if (n.nodeType === 1 && (n.matches('[data-compare-toggle]') || n.querySelector('[data-compare-toggle]'))) {
        queued = true; requestAnimationFrame(() => { queued = false; syncToggles(); });
        return;
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncToggles); else syncToggles();

  /* ---------- floating tray ---------- */
  class LoupeCompareTray extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.s = strings(this);
      this.slots = this.querySelector('[data-compare-slots]');
      this.go = this.querySelector('[data-compare-go]');
      this.countEl = this.querySelector('[data-compare-count]');
      this.note = this.querySelector('[data-compare-status]');
      this.toggle = this.querySelector('[data-compare-collapse]');
      this.base = this.dataset.compareUrl || '/pages/compare';
      this.hidden = false;
      try { this.collapsed = sessionStorage.getItem('loupe:compare-collapsed') === '1'; } catch (e) { this.collapsed = false; }
      // Phones: start folded on every page so the tray never covers the grid; adding a product opens it.
      if (matchMedia('(max-width: 749px)').matches) this.collapsed = true;

      this.onChange = () => this.render();
      document.addEventListener('loupe:compare-changed', this.onChange);
      this.onSelect = (e) => {
        if (!e.target.contains(this)) return;
        this.forced = e.type === 'shopify:section:select'; this.render();
      };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onSelect);

      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-compare-remove]');
        if (rm) {
          const h = rm.dataset.compareRemove, item = read().find((x) => x.handle === h);
          const i = [...this.slots.querySelectorAll('[data-compare-remove]')].indexOf(rm);
          api.remove(h);
          this.say(fill(this.s.removed, { title: item ? item.title : h }));
          const next = this.slots.querySelectorAll('[data-compare-remove]');
          (next[Math.min(i, next.length - 1)] || this.go).focus();
          return;
        }
        if (e.target.closest('[data-compare-clear]')) { api.clear(); this.say(this.s.cleared); return; }
        if (e.target.closest('[data-compare-collapse]')) { this.setCollapsed(!this.collapsed); return; }
        if (e.target.closest('[data-compare-go]') && read().length < 2) { e.preventDefault(); this.say(this.s.need_more); }
      });
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('loupe:compare-changed', this.onChange);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onSelect);
    }

    setCollapsed(on) {
      this.collapsed = on;
      try { sessionStorage.setItem('loupe:compare-collapsed', on ? '1' : '0'); } catch (e) {}
      this.render();
      if (this.toggle) this.toggle.focus();
    }

    say(msg) {
      if (!this.note || !msg) return;
      this.note.textContent = msg; this.note.hidden = false;
      clearTimeout(this._t); this._t = setTimeout(() => { this.note.hidden = true; this.note.textContent = ''; }, 4000);
    }

    nudge() {
      if (this.collapsed) this.setCollapsed(false);
      this.classList.remove('is-nudged'); void this.offsetWidth; this.classList.add('is-nudged');
    }

    render() {
      const items = read();
      const onComparePage = !!document.querySelector('loupe-compare-page');
      const relevant = this.dataset.everywhere === 'true' || !!document.querySelector('[data-compare-toggle]');
      const open = this.forced || (items.length > 0 && relevant && !onComparePage);
      this.classList.toggle('is-open', open);
      this.toggleAttribute('inert', !open);
      this.classList.toggle('is-collapsed', !!this.collapsed);
      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', String(!this.collapsed));
        this.toggle.setAttribute('aria-label', this.collapsed ? this.s.show : this.s.hide);
      }
      if (this.countEl) this.countEl.textContent = fill(this.s.count, { n: items.length, max: MAX });

      const frag = document.createDocumentFragment();
      for (let i = 0; i < MAX; i++) {
        const it = items[i], li = document.createElement('li');
        li.className = 'compare-tray__slot';
        if (!it) { li.classList.add('is-empty'); li.setAttribute('aria-hidden', 'true'); frag.append(li); continue; }
        const a = document.createElement(it.url ? 'a' : 'span');
        a.className = 'compare-tray__thumb';
        if (it.url) a.href = it.url;
        a.title = it.title;
        if (it.image) {
          const img = document.createElement('img');
          img.src = it.image; img.alt = it.title; img.width = 120; img.height = 120; img.loading = 'lazy';
          a.append(img);
        } else {
          const t = document.createElement('span'); t.className = 'compare-tray__initial'; t.textContent = (it.title || '?').trim().charAt(0); a.append(t);
          const v = document.createElement('span'); v.className = 'visually-hidden'; v.textContent = it.title; a.append(v);
        }
        const rm = document.createElement('button');
        rm.type = 'button'; rm.className = 'compare-tray__remove'; rm.dataset.compareRemove = it.handle;
        rm.setAttribute('aria-label', fill(this.s.remove, { title: it.title }));
        rm.innerHTML = iconClose;
        li.append(a, rm); frag.append(li);
      }
      this.slots.replaceChildren(frag);

      if (this.go) {
        const ready = items.length >= 2;
        const sep = this.base.includes('?') ? '&' : '?';
        this.go.href = items.length ? `${this.base}${sep}products=${items.map((x) => encodeURIComponent(x.handle)).join(',')}` : this.base;
        this.go.setAttribute('aria-disabled', String(!ready));
        this.go.classList.toggle('is-disabled', !ready);
      }
    }
  }
  if (!customElements.get('loupe-compare-tray')) customElements.define('loupe-compare-tray', LoupeCompareTray);

  /* ---------- comparison page ---------- */
  const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
  const norm = (n) => (n ? n.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : '');

  class LoupeComparePage extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.s = strings(this);
      this.rows = [...this.querySelectorAll('[data-compare-rows] > li')].map((li) => ({ key: li.dataset.compareRow, label: li.textContent.trim() }));
      this.out = this.querySelector('[data-compare-table]');
      this.status = this.querySelector('[data-compare-status]');
      this.toolbar = this.querySelector('[data-compare-toolbar]');
      this.diff = this.querySelector('[data-compare-diff]');

      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-compare-remove]'); if (rm) return this.remove(rm.dataset.compareRemove, rm);
        if (e.target.closest('[data-compare-clear]')) return this.clearAll();
        if (e.target.closest('[data-compare-retry]')) return this.load();
      });
      if (this.diff) this.diff.addEventListener('change', () => this.applyDiff());

      const param = new URLSearchParams(location.search).get('products');
      this.fromUrl = !!param;
      let handles = param ? param.split(',').map((h) => h.trim()) : api.items().map((x) => x.handle);
      handles = [...new Set(handles.filter(okHandle))].slice(0, MAX);
      if (!handles.length && this.dataset.demo) {   // theme editor: preview with real products
        this.demo = true;
        handles = this.dataset.demo.split(',').filter(okHandle).slice(0, 3);
      }
      this.handles = handles;
      handles.length ? this.load() : this.show('empty');
    }

    show(state) {
      ['loading', 'empty', 'error'].forEach((n) => { const x = this.querySelector(`[data-compare-${n}]`); if (x) x.hidden = n !== state; });
      this.out.hidden = state !== 'table';
      if (this.toolbar) this.toolbar.hidden = state !== 'table';
      this.setAttribute('aria-busy', String(state === 'loading'));
    }

    async load() {
      this.show('loading');
      const mf = this.rows.filter((r) => r.key.startsWith('mf:')).map((r) => r.key);
      const q = this.handles.map((h) => `handle:${h}`).concat(mf).join(' OR ');
      try {
        const early = window.__loupeCompare && window.__loupeCompare.q === q ? window.__loupeCompare.req : null;
        if (window.__loupeCompare) window.__loupeCompare = null;   // use the early request once (retries fetch fresh)
        const r = await (early || fetch(`${this.dataset.url}?q=${encodeURIComponent(q)}&type=product&section_id=main-compare`, { credentials: 'same-origin' }));
        if (!r.ok) throw new Error(String(r.status));
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        this.products = [...doc.querySelectorAll('[data-compare-products] > [data-compare-product]')];
        if (!this.demo && this.products.length) {
          // keep the tray in step: drop products that no longer exist, adopt a shared link's selection
          api.set(this.products.map((p) => ({ handle: p.dataset.handle, title: p.dataset.title, image: p.dataset.image, url: p.dataset.url })));
        }
        if (!this.products.length) return this.show('empty');
        this.build();
      } catch (e) {
        this.show('error');
      }
    }

    cells(p, key) { return [...p.children].filter((c) => c.dataset.cell === key); }

    build() {
      const P = this.products, n = P.length;
      const table = el('table', 'pc__table');
      table.style.setProperty('--cols', n);
      const cap = el('caption', 'visually-hidden', this.s.caption); table.append(cap);

      /* head: product image, title, price, remove */
      const thead = el('thead'), htr = el('tr');
      const corner = el('td', 'pc__label pc__corner');
      corner.append(el('span', 'visually-hidden', this.s.feature));
      htr.append(corner);
      P.forEach((p) => {
        const th = el('th', 'pc__product'); th.scope = 'col';
        const src = this.cells(p, 'product')[0];
        if (src) th.append(...[...src.childNodes].map((c) => document.importNode(c, true)));
        if (!this.demo) {
          const rm = el('button', 'pc__remove'); rm.type = 'button'; rm.dataset.compareRemove = p.dataset.handle;
          rm.setAttribute('aria-label', fill(this.s.remove, { title: p.dataset.title }));
          rm.innerHTML = iconClose;
          th.append(rm);
        }
        htr.append(th);
      });
      thead.append(htr); table.append(thead);

      /* body rows in the merchant's order; "options" expands into one row per option name */
      const tbody = el('tbody');
      const addRow = (label, pick) => {
        const tr = el('tr'), th = el('th', 'pc__label', label); th.scope = 'row'; tr.append(th);
        const vals = P.map((p) => {
          const td = el('td'), c = pick(p);
          if (c && c.textContent.trim()) td.append(...[...c.childNodes].map((x) => document.importNode(x, true)));
          else { td.append(el('span', 'pc__none', '—')); td.lastChild.setAttribute('aria-hidden', 'true'); td.append(el('span', 'visually-hidden', this.s.none)); }
          tr.append(td); return norm(c);
        });
        if (vals.every((v) => !v)) return;   // nobody has a value (e.g. metafield not set up): skip the row
        if (n > 1 && vals.every((v) => v === vals[0])) tr.dataset.same = '';
        tbody.append(tr);
      };
      this.rows.forEach((row) => {
        if (row.key === 'options') {
          const names = [];
          P.forEach((p) => this.cells(p, 'opt').forEach((c) => {
            const nm = c.dataset.name || ''; if (nm && !names.some((x) => x.toLowerCase() === nm.toLowerCase())) names.push(nm);
          }));
          names.forEach((nm) => addRow(nm, (p) => this.cells(p, 'opt').find((c) => (c.dataset.name || '').toLowerCase() === nm.toLowerCase())));
        } else {
          addRow(row.label, (p) => this.cells(p, row.key)[0]);
        }
      });
      if (tbody.children.length) table.append(tbody);

      /* foot: add to cart + view */
      const tfoot = el('tfoot'), ftr = el('tr');
      ftr.append(el('td', 'pc__label'));
      P.forEach((p) => {
        const td = el('td', 'pc__actions'), src = this.cells(p, 'actions')[0];
        if (src) td.append(...[...src.childNodes].map((c) => document.importNode(c, true)));
        ftr.append(td);
      });
      tfoot.append(ftr); table.append(tfoot);

      const scroll = el('div', 'pc__scroll');
      scroll.tabIndex = 0; scroll.setAttribute('role', 'region'); scroll.setAttribute('aria-label', this.s.caption);
      scroll.append(table);
      this.out.replaceChildren(scroll);
      const hint = this.querySelector('[data-compare-hint]'); if (hint) hint.hidden = n < 2;
      this.show('table');
      this.applyDiff();
    }

    applyDiff() {
      const on = !!(this.diff && this.diff.checked);
      this.classList.toggle('is-diff', on);
    }

    syncUrl() {
      if (!this.fromUrl) return;
      const u = new URL(location.href);
      if (this.handles.length) u.searchParams.set('products', this.handles.join(',')); else u.searchParams.delete('products');
      history.replaceState(history.state, '', u.pathname + (u.search ? u.search.replace(/%2C/gi, ',') : '') + u.hash);
    }

    remove(h, btn) {
      const p = (this.products || []).find((x) => x.dataset.handle === h);
      const all = [...this.querySelectorAll('[data-compare-remove]')], i = all.indexOf(btn);
      this.products = (this.products || []).filter((x) => x.dataset.handle !== h);
      this.handles = this.handles.filter((x) => x !== h);
      api.remove(h);
      this.syncUrl();
      this.say(fill(this.s.removed, { title: p ? p.dataset.title : h }));
      if (!this.products.length) { this.show('empty'); const e = this.querySelector('[data-compare-empty] a, [data-compare-empty] h2'); e && e.focus && e.focus(); return; }
      this.build();
      const next = this.querySelectorAll('[data-compare-remove]');
      (next[Math.min(Math.max(i, 0), next.length - 1)] || this.out).focus();
    }

    clearAll() {
      api.clear();
      this.products = []; this.handles = [];
      this.syncUrl();
      this.say(this.s.cleared);
      this.show('empty');
    }

    say(msg) { if (this.status && msg) { this.status.textContent = ''; requestAnimationFrame(() => (this.status.textContent = msg)); } }
  }
  if (!customElements.get('loupe-compare-page')) customElements.define('loupe-compare-page', LoupeComparePage);
})();
